import clsx from 'clsx'

/**
 * Surfaces.
 *
 * A single flat card on a sunken background, separated by a hairline border
 * rather than a shadow. Shadows on every panel of a data-dense screen create
 * visual noise that competes with the data; the border does the same job at a
 * fraction of the weight.
 */

export function Card({ children, className, as: Component = 'div', ...props }) {
  return (
    <Component
      className={clsx(
        'rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)]',
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  )
}

export function CardHeader({ title, description, actions, icon: Icon, className }) {
  return (
    <div
      className={clsx(
        // Wraps on narrow screens. A title plus two or three action controls
        // cannot share one line at 375px, and `shrink-0` on the actions means
        // the row pushes the page sideways rather than folding — which showed
        // up as a horizontal scrollbar on every card that has buttons.
        'flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b border-[var(--color-line)] px-5 py-4',
        className,
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} className="text-[var(--color-ink-500)] shrink-0" aria-hidden="true" />}
          <h2 className="text-[0.9375rem] font-semibold text-[var(--color-ink-900)] truncate">
            {title}
          </h2>
        </div>

        {description && (
          <p className="mt-0.5 text-sm text-[var(--color-ink-500)]">{description}</p>
        )}
      </div>

      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

export function CardBody({ children, className, padded = true }) {
  return <div className={clsx(padded && 'px-5 py-4', className)}>{children}</div>
}

export function CardFooter({ children, className }) {
  return (
    <div
      className={clsx(
        'flex items-center justify-end gap-2 border-t border-[var(--color-line)]',
        'bg-[var(--color-surface-sunken)] px-5 py-3 rounded-b-lg',
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * A single headline metric.
 *
 * `tone` is reserved for numbers that mean something is wrong. A dashboard
 * where every tile is coloured teaches the reader to ignore colour, so the
 * default is deliberately plain and only overdue-style figures deviate.
 */
export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
  icon: Icon,
  trend,
  onClick,
  className,
}) {
  const tones = {
    neutral: 'var(--color-ink-900)',
    danger:  'var(--color-danger)',
    warning: 'var(--color-warning)',
    success: 'var(--color-success)',
  }

  const interactive = typeof onClick === 'function'
  const Component = interactive ? 'button' : 'div'

  return (
    <Component
      onClick={onClick}
      type={interactive ? 'button' : undefined}
      className={clsx(
        'rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-4 py-3.5 text-left',
        interactive &&
          'cursor-pointer transition-colors duration-150 hover:border-[var(--color-line-strong)] hover:bg-[var(--color-surface-sunken)]',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-ink-500)]">
          {label}
        </span>
        {Icon && <Icon size={15} className="text-[var(--color-ink-400)] shrink-0" aria-hidden="true" />}
      </div>

      <div className="mt-1.5 flex items-baseline gap-2">
        {/* Tabular figures: a row of tiles whose numbers change should not
            shimmer as digit widths differ. */}
        <span
          className="font-display text-2xl font-semibold tnum leading-none"
          style={{ color: tones[tone] ?? tones.neutral }}
        >
          {value}
        </span>

        {trend && (
          <span
            className="text-xs font-medium tnum"
            style={{ color: trend.positive ? 'var(--color-success)' : 'var(--color-danger)' }}
          >
            {trend.positive ? '+' : ''}{trend.value}
          </span>
        )}
      </div>

      {hint && <p className="mt-1 text-xs text-[var(--color-ink-500)]">{hint}</p>}
    </Component>
  )
}

/**
 * Section heading for a page.
 *
 * Kept as a component so heading levels stay sequential across screens — a
 * skipped level is one of the easiest accessibility regressions to introduce
 * and one of the hardest to notice.
 */
export function SectionHeading({ title, description, actions, level = 2, className }) {
  const Heading = `h${level}`

  return (
    <div className={clsx('flex items-end justify-between gap-4', className)}>
      <div className="min-w-0">
        <Heading className="text-base font-semibold text-[var(--color-ink-900)]">{title}</Heading>
        {description && (
          <p className="mt-0.5 text-sm text-[var(--color-ink-500)]">{description}</p>
        )}
      </div>

      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  )
}
